import { LibraryItem, LibraryCollection } from "@/components/quotation/types";
import { libraryService as supabaseLibraryService } from "./supabase/library";

// Constantes para as chaves do localStorage
const LIBRARY_ITEMS_KEY = 'voah_library_items';
const LIBRARY_COLLECTIONS_KEY = 'voah_library_collections';

// Cache local para itens e coleções
let itemsCache: LibraryItem[] | null = null;
let collectionsCache: LibraryCollection[] | null = null;
let itemsCacheTimestamp: number | null = null;
const CACHE_TTL = 300000; // 5 minutos de duração do cache (aumentado de 30s para 5min)

// Controle de requisições simultâneas
let fetchItemsPromise: Promise<LibraryItem[]> | null = null;
let fetchingItems = false;

// Funções utilitárias para localStorage como fallback
const loadItemsFromLocalStorage = (): LibraryItem[] => {
  try {
    const savedItems = localStorage.getItem(LIBRARY_ITEMS_KEY);
    if (!savedItems) return [];
    
    return JSON.parse(savedItems, (key, value) => {
      // Converter strings de data para objetos Date
      if (key === 'createdAt' || key === 'updatedAt') {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Erro ao carregar itens da biblioteca (localStorage):', error);
    return [];
  }
};

const saveItemsToLocalStorage = (items: LibraryItem[]): boolean => {
  try {
    localStorage.setItem(LIBRARY_ITEMS_KEY, JSON.stringify(items));
    return true;
  } catch (error) {
    console.error('Erro ao salvar itens na biblioteca (localStorage):', error);
    return false;
  }
};

const loadCollectionsFromLocalStorage = (): LibraryCollection[] => {
  try {
    const savedCollections = localStorage.getItem(LIBRARY_COLLECTIONS_KEY);
    if (!savedCollections) return [];
    
    return JSON.parse(savedCollections, (key, value) => {
      // Converter strings de data para objetos Date
      if (key === 'createdAt' || key === 'updatedAt') {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Erro ao carregar coleções da biblioteca (localStorage):', error);
    return [];
  }
};

const saveCollectionsToLocalStorage = (collections: LibraryCollection[]): boolean => {
  try {
    localStorage.setItem(LIBRARY_COLLECTIONS_KEY, JSON.stringify(collections));
    return true;
  } catch (error) {
    console.error('Erro ao salvar coleções na biblioteca (localStorage):', error);
    return false;
  }
};

// Exportação do serviço da biblioteca
export const LibraryService = {
  // Métodos para gerenciar itens
  saveItem: async (item: LibraryItem): Promise<LibraryItem> => {
    try {
      // For Supabase, we need to create a new object without id/createdAt/updatedAt
      // because Supabase will generate its own ID or fail on conflict
      
      // Check if the item already exists in Supabase
      let existingItem = null;
      if (item.id) {
        try {
          existingItem = await supabaseLibraryService.getItemById(item.id);
        } catch (error) {
          // Logging removed
        }
      }

      let savedItem;
      if (existingItem) {
        // Update existing item
        // Logging removed
        savedItem = await supabaseLibraryService.updateItem(item.id, {
          type: item.type,
          title: item.title,
          description: item.description,
          data: item.data,
          tags: item.tags,
          thumbnailUrl: item.thumbnailUrl,
          location: item.location
        });
      } else {
        // Create completely new item with a new ID
        // Logging removed
        savedItem = await supabaseLibraryService.saveItem({
          type: item.type,
          title: item.title,
          description: item.description,
          data: item.data,
          tags: item.tags,
          thumbnailUrl: item.thumbnailUrl,
          location: item.location
        });
        
        // Logging removed
      }
      
      // Limpar cache para forçar recarga dos dados
      itemsCache = null;
      
      return savedItem;
    } catch (error) {
      console.error('Erro ao salvar item no Supabase:', error);
      throw error;
    }
  },
  
  getItems: async (filters?: Partial<LibraryItem>): Promise<LibraryItem[]> => {
    try {
      // Verificar se o cache está válido (menos de 5 minutos)
      const now = Date.now();
      const isCacheValid = itemsCache && itemsCacheTimestamp && (now - itemsCacheTimestamp < CACHE_TTL);
      
      // MODIFICADO: Verificar se estamos em modo Incognito
      const isIncognito = !window.localStorage.getItem;
      
      // Se já temos cache válido, usar o cache
      if (isCacheValid && !isIncognito) {
        // Se temos filtros, aplicamos nos itens em cache
        if (filters && itemsCache) {
          return filterItems(itemsCache, filters);
        }
        
        // Sem filtros, retornar o cache completo
        return itemsCache;
      }

      // Verificar se uma requisição já está em andamento 
      // e reutilizá-la em vez de iniciar uma nova
      if (fetchingItems && fetchItemsPromise && !isIncognito) {
        // Aguardar a requisição em andamento terminar
        const items = await fetchItemsPromise;
        
        // Aplicar filtros se necessário
        if (filters) {
          return filterItems(items, filters);
        }
        return items;
      }

      // Iniciar nova requisição e armazenar a promise
      fetchingItems = true;
      fetchItemsPromise = (async () => {
        try {
          const items = await supabaseLibraryService.getItems();
          
          // Atualizar cache com timestamp
          if (!isIncognito) {
            itemsCache = items;
            itemsCacheTimestamp = now;
          }
          
          return items;
        } catch (error) {
          console.error('Erro ao buscar itens do Supabase:', error);
          
          // Fallback para localStorage em caso de erro
          return loadItemsFromLocalStorage();
        } finally {
          // Limpar estado de requisição em andamento
          fetchingItems = false;
          fetchItemsPromise = null;
        }
      })();
      
      // Aguardar a requisição e aplicar filtros se necessário
      const items = await fetchItemsPromise;
      
      if (filters) {
        return filterItems(items, filters);
      }
      return items;
    } catch (error) {
      console.error('Erro ao processar requisição de itens:', error);
      
      // Fallback para localStorage
      const items = loadItemsFromLocalStorage();
      
      if (filters) {
        return filterItems(items, filters);
      }
      return items;
    }
  },
  
  getItemById: async (id: string): Promise<LibraryItem | null> => {
    try {
      // Verificar primeiro no cache
      if (itemsCache) {
        const cachedItem = itemsCache.find(item => item.id === id);
        if (cachedItem) {
          return cachedItem;
        }
      }
      
      // Se não estiver no cache, buscar do Supabase
      return await supabaseLibraryService.getItemById(id);
    } catch (error) {
      console.error(`Erro ao buscar item ${id} do Supabase:`, error);
      
      // Fallback para localStorage
      const items = loadItemsFromLocalStorage();
      const item = items.find(i => i.id === id);
      return item || null;
    }
  },
  
  updateItem: async (id: string, updates: Partial<LibraryItem>): Promise<boolean> => {
    try {
      // Atualizar no Supabase
      await supabaseLibraryService.updateItem(id, updates);
      
      // Em vez de invalidar o cache completamente, atualizamos apenas o item modificado
      if (itemsCache && itemsCacheTimestamp) {
        const itemIndex = itemsCache.findIndex(item => item.id === id);
        if (itemIndex >= 0) {
          itemsCache[itemIndex] = { ...itemsCache[itemIndex], ...updates };
        }
      }
      
      return true;
    } catch (error) {
      console.error(`Erro ao atualizar item ${id} no Supabase:`, error);
      
      // Fallback para localStorage
      const items = loadItemsFromLocalStorage();
      const index = items.findIndex(i => i.id === id);
      
      if (index < 0) return false;
      
      items[index] = {
        ...items[index],
        ...updates,
        updatedAt: new Date()
      };
      
      return saveItemsToLocalStorage(items);
    }
  },
  
  deleteItem: async (id: string): Promise<boolean> => {
    try {
      // Excluir do Supabase
      await supabaseLibraryService.deleteItem(id);
      
      // Em vez de invalidar o cache completamente, removemos apenas o item excluído
      if (itemsCache && itemsCacheTimestamp) {
        itemsCache = itemsCache.filter(item => item.id !== id);
      }
      
      return true;
    } catch (error) {
      console.error(`Erro ao excluir item ${id} do Supabase:`, error);
      
      // Fallback para localStorage
      const items = loadItemsFromLocalStorage();
      const filteredItems = items.filter(i => i.id !== id);
      
      if (filteredItems.length === items.length) {
        // Nenhum item foi removido
        return false;
      }
      
      // Também remover o item de todas as coleções
      const collections = loadCollectionsFromLocalStorage();
      let collectionsChanged = false;
      
      const updatedCollections = collections.map(collection => {
        if (collection.items.includes(id)) {
          collectionsChanged = true;
          return {
            ...collection,
            items: collection.items.filter(itemId => itemId !== id)
          };
        }
        return collection;
      });
      
      // Salvar as coleções atualizadas se houver mudanças
      if (collectionsChanged) {
        saveCollectionsToLocalStorage(updatedCollections);
      }
      
      return saveItemsToLocalStorage(filteredItems);
    }
  },
  
  // Métodos para gerenciar coleções
  createCollection: async (
    name: string, 
    description: string = '', 
    items: string[] = []
  ): Promise<LibraryCollection> => {
    try {
      // Criar no Supabase
      const collection = await supabaseLibraryService.createCollection(name, description, items);
      
      // Limpar cache para forçar recarga dos dados
      collectionsCache = null;
      
      return collection;
    } catch (error) {
      console.error('Erro ao criar coleção no Supabase:', error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      
      const newCollection: LibraryCollection = {
        id: `collection_${Date.now()}`,
        name,
        description,
        items,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      collections.push(newCollection);
      saveCollectionsToLocalStorage(collections);
      
      return newCollection;
    }
  },
  
  getCollections: async (): Promise<LibraryCollection[]> => {
    try {
      // Se já temos cache, retornar o cache
      if (collectionsCache) {
        return collectionsCache;
      }

      // Buscar do Supabase
      const collections = await supabaseLibraryService.getCollections();
      
      // Atualizar cache
      collectionsCache = collections;
      
      return collections;
    } catch (error) {
      console.error('Erro ao buscar coleções do Supabase:', error);
      
      // Fallback para localStorage
      return loadCollectionsFromLocalStorage();
    }
  },
  
  getCollectionById: async (id: string): Promise<LibraryCollection | null> => {
    try {
      // Buscar do Supabase
      return await supabaseLibraryService.getCollectionById(id);
    } catch (error) {
      console.error(`Erro ao buscar coleção ${id} do Supabase:`, error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      const collection = collections.find(c => c.id === id);
      return collection || null;
    }
  },
  
  updateCollection: async (id: string, updates: Partial<LibraryCollection>): Promise<boolean> => {
    try {
      // Atualizar no Supabase
      await supabaseLibraryService.updateCollection(id, updates);
      
      // Limpar cache para forçar recarga dos dados
      collectionsCache = null;
      
      return true;
    } catch (error) {
      console.error(`Erro ao atualizar coleção ${id} no Supabase:`, error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      const index = collections.findIndex(c => c.id === id);
      
      if (index < 0) return false;
      
      collections[index] = {
        ...collections[index],
        ...updates,
        updatedAt: new Date()
      };
      
      return saveCollectionsToLocalStorage(collections);
    }
  },
  
  deleteCollection: async (id: string): Promise<boolean> => {
    try {
      // Excluir do Supabase
      await supabaseLibraryService.deleteCollection(id);
      
      // Limpar cache para forçar recarga dos dados
      collectionsCache = null;
      
      return true;
    } catch (error) {
      console.error(`Erro ao excluir coleção ${id} do Supabase:`, error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      const filteredCollections = collections.filter(c => c.id !== id);
      
      if (filteredCollections.length === collections.length) {
        // Nenhuma coleção foi removida
        return false;
      }
      
      return saveCollectionsToLocalStorage(filteredCollections);
    }
  },
  
  // Métodos para gerenciar itens em coleções
  addItemToCollection: async (itemId: string, collectionId: string): Promise<boolean> => {
    try {
      // Primeiro, obter a coleção do Supabase
      const collection = await supabaseLibraryService.getCollectionById(collectionId);
      
      if (!collection) return false;
      
      // Verificar se o item já está na coleção
      if (collection.items.includes(itemId)) {
        return true; // Item já está na coleção
      }
      
      // Adicionar o item e atualizar a coleção
      const updatedItems = [...collection.items, itemId];
      await supabaseLibraryService.updateCollection(collectionId, { items: updatedItems });
      
      // Limpar cache para forçar recarga dos dados
      collectionsCache = null;
      
      return true;
    } catch (error) {
      console.error(`Erro ao adicionar item ${itemId} à coleção ${collectionId} no Supabase:`, error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      const collectionIndex = collections.findIndex(c => c.id === collectionId);
      
      if (collectionIndex < 0) return false;
      
      // Verificar se o item já está na coleção
      if (collections[collectionIndex].items.includes(itemId)) {
        return true; // Item já está na coleção
      }
      
      // Adicionar o item à coleção
      collections[collectionIndex].items.push(itemId);
      collections[collectionIndex].updatedAt = new Date();
      
      return saveCollectionsToLocalStorage(collections);
    }
  },
  
  removeItemFromCollection: async (itemId: string, collectionId: string): Promise<boolean> => {
    try {
      // Primeiro, obter a coleção do Supabase
      const collection = await supabaseLibraryService.getCollectionById(collectionId);
      
      if (!collection) return false;
      
      // Verificar se o item está na coleção
      if (!collection.items.includes(itemId)) {
        return true; // Item não estava na coleção
      }
      
      // Remover o item e atualizar a coleção
      const updatedItems = collection.items.filter(id => id !== itemId);
      await supabaseLibraryService.updateCollection(collectionId, { items: updatedItems });
      
      // Limpar cache para forçar recarga dos dados
      collectionsCache = null;
      
      return true;
    } catch (error) {
      console.error(`Erro ao remover item ${itemId} da coleção ${collectionId} no Supabase:`, error);
      
      // Fallback para localStorage
      const collections = loadCollectionsFromLocalStorage();
      const collectionIndex = collections.findIndex(c => c.id === collectionId);
      
      if (collectionIndex < 0) return false;
      
      // Remover o item da coleção
      collections[collectionIndex].items = collections[collectionIndex].items.filter(id => id !== itemId);
      collections[collectionIndex].updatedAt = new Date();
      
      return saveCollectionsToLocalStorage(collections);
    }
  },
  
  // Função para atualizar um item no cache
  updateItemInCache: (id: string, updates: Partial<LibraryItem>) => {
    if (itemsCache) {
      const itemIndex = itemsCache.findIndex(item => item.id === id);
      if (itemIndex >= 0) {
        itemsCache[itemIndex] = { ...itemsCache[itemIndex], ...updates };
        // Logging removed: Item atualizado no cache local
      }
    }
  },
  
  // Função para remover um item do cache
  removeItemFromCache: (id: string) => {
    if (itemsCache && itemsCacheTimestamp) {
      itemsCache = itemsCache.filter(item => item.id !== id);
      // Logging removed: Item removido do cache local
    }
  },
  
  // Função para invalidar explicitamente o cache quando necessário
  invalidateCache: (): void => {
    itemsCache = null;
    itemsCacheTimestamp = null;
    collectionsCache = null;
  },
  
  // Função para atualizar o cache sem buscar novamente (para otimizações)
  updateCache: (newItems: LibraryItem[]) => {
    itemsCache = [...newItems];
    itemsCacheTimestamp = Date.now();
  },
  
  // Função para atualizar um item no cache local
  updateItemInLocalCache: (item: LibraryItem): void => {
    try {
      if (!itemsCache) return;
      
      // Encontrar o índice do item no cache
      const index = itemsCache.findIndex(cachedItem => cachedItem.id === item.id);
      
      if (index !== -1) {
        // Substituir o item no cache
        itemsCache[index] = item;
      } else {
        // Adicionar ao cache se não existe
        itemsCache.push(item);
      }
      
      // Atualizar timestamp do cache
      itemsCacheTimestamp = Date.now();
    } catch (error) {
      console.error('Erro ao atualizar item no cache local:', error);
    }
  },
  
  // Função para remover um item do cache local
  removeItemFromLocalCache: (itemId: string): void => {
    try {
      if (!itemsCache) return;
      
      // Filtrar o cache para remover o item
      itemsCache = itemsCache.filter(item => item.id !== itemId);
      
      // Atualizar timestamp do cache
      itemsCacheTimestamp = Date.now();
    } catch (error) {
      console.error('Erro ao remover item do cache local:', error);
    }
  },
  
  updateCacheTimestamp: (): void => {
    if (itemsCache) {
      itemsCacheTimestamp = Date.now();
    }
  }
};

// Extrair a função de filtragem para reutilização
function filterItems(items: LibraryItem[], filters: Partial<LibraryItem>): LibraryItem[] {
  return items.filter(item => {
    for (const [key, value] of Object.entries(filters)) {
      // Tratamento especial para campos aninhados como location.country
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        // @ts-ignore - Acesso dinâmico a propriedades aninhadas
        if (item[parent]?.[child] !== value) return false;
      } 
      // Tratamento para campos simples
      else if (item[key as keyof LibraryItem] !== value) {
        return false;
      }
    }
    return true;
  });
} 